import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '../lib/monitoring';

interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
}

/**
 * Top-level error boundary.
 *
 * A React render crash otherwise leaves a blank white page, which on a job site
 * is indistinguishable from a dead app. This shows something truthful and
 * offers the one action that reliably helps.
 *
 * The error is reported through the monitoring module, which is a no-op when no
 * Sentry DSN is configured — the normal state today.
 */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, `react.render${info.componentStack ? '' : ''}`);
  }

  override render() {
    if (!this.state.failed) return this.props.children;

    return (
      <main className="cl-screen cl-centered">
        <h1 className="auth-wordmark">Something went wrong</h1>
        <p className="cl-notice" role="alert">
          Crewlink hit an unexpected problem and stopped. Reloading usually fixes it. Nothing you
          had saved is affected.
        </p>
        <div className="auth-actions">
          <button className="cl-button" type="button" onClick={() => window.location.reload()}>
            Reload Crewlink
          </button>
        </div>
      </main>
    );
  }
}
